import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import TextField from "@/common/components/TextField";
import { FormTransformers } from "@/common/utils/formTransformers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { usePrinterMachineDisable } from "../../../../common/hooks/queries/usePrinterMachineDisable";
import { usePrinterMachineEnable } from "../../../../common/hooks/queries/usePrinterMachineEnable";
import type { PrinterMachineData } from "../../../../common/types/printerMachine.type";
import {
  printerMachineDisableSchema,
  type PrinterMachineDisableValidationSchema
} from "../../schemas/printerMachineActions.schema";

const DisableEnablePrinterMachine = ({ printer }: { printer: PrinterMachineData }) => {
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [disableModalIsOpen, setDisableModalIsOpen] = useState(false);
  const [enableModalIsOpen, setEnableModalIsOpen] = useState(false);
  const [isError, setIsError] = useState<boolean>(false);

  const {
    register,
    setError,
    handleSubmit,
    formState: { errors },
  } = useForm<PrinterMachineDisableValidationSchema>({
    resolver: zodResolver(printerMachineDisableSchema),
  });

  const { mutate: disablePrinter, isPending: isDisabling } = usePrinterMachineDisable({
    printerMachineId: printer.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['printerMachine', printer.id] });
      setIsError(false);
      setDisableModalIsOpen(false);
      setMessages([`Impressora ${printer.display_name} desativada com sucesso.`]);
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError })(error);
      setMessages((error.response?.data as { errors: { base: string[] } })?.errors?.base || null);
    },
  });

  const { mutate: enablePrinter, isPending: isEnabling } = usePrinterMachineEnable({
    printerMachineId: printer.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['printerMachine', printer.id] });
      setIsError(false);
      setEnableModalIsOpen(false);
      setMessages([`Impressora ${printer.display_name} reativada com sucesso.`]);
    },
    onError: (error) => {
      setMessages((error.response?.data as { errors: { base: string[] } })?.errors?.base || null);
    },
  });

  const isDisabled = printer.status === "disabled";

  return (
    <>
      {!isDisabled ? (
        <Modal
          Trigger={(
            <Button
              operationKlassToCheckPermission="Admin::Printer::Machines::Disable" isLoading={isDisabling} type="button"
              variant="error" name="printer_machine_disable">
              Desativar impressora
            </Button>
          )}
          title="Você deseja realmente desativar esta impressora?"
          subtitle="Essa ação impedirá a impressora de executar novas tarefas."
          subtitleClassName="mt-1 text-white-300"
          showClose={false}
          isOpen={disableModalIsOpen}
          setIsOpen={setDisableModalIsOpen}
          useCancellableFooter
          onConfirm={handleSubmit((data) => disablePrinter(data))}
          body={(
            <form className="flex flex-col gap-4">
              <TextField
                label="Motivo"
                markAsRequired
                {...register("disable_reason")}
                errorMessage={errors.disable_reason?.message}
              />
            </form>
          )}
        />
      ) : (
        <Modal
          Trigger={(
            <Button
              operationKlassToCheckPermission="Admin::Printer::Machines::Enable" type="button" isLoading={isEnabling}
              name="printer_machine_enable">
              Reativar impressora
            </Button>
          )}
          title="Você deseja realmente reativar esta impressora?"
          subtitle="A impressora será reativada e voltará a funcionar normalmente."
          subtitleClassName="mt-2 text-white-300"
          confirmButtonClassName="bg-blue-700"
          showClose={false}
          isOpen={enableModalIsOpen}
          setIsOpen={setEnableModalIsOpen}
          useCancellableFooter
          onConfirm={() => enablePrinter()}
        />
      )}

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={isError}
        />
      )}
    </>
  );
};

export default DisableEnablePrinterMachine;